import type { FC, MutableRefObject } from "react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from "react";

import { useQueryClient } from "@tanstack/react-query";

import { CONVERSATIONS_QUERY_KEY } from "@/api/agenticAI/useConversationsApi";
import axiosInstance from "@/api/config";
import {
  BarChartIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon
} from "@/assets/icons";
import { REPORT_CATALOG_ID } from "@/components/analysisReport/a2ui/contract";
import type { ReportSurfaceStore } from "@/components/analysisReport/a2ui/surfaceStore";
import { createReportSurfaceStore } from "@/components/analysisReport/a2ui/surfaceStore";
import { useAnalysisReportAdapter } from "@/components/analysisReport/a2ui/useAnalysisReportAdapter";
import { AnalysisReportPanel } from "@/components/analysisReport/AnalysisReportPanel";
import { ReportArtifactCard } from "@/components/analysisReport/ReportArtifactCard";
import { useReportPdfExport } from "@/components/analysisReport/useReportPdfExport";
import { useAgenticAI } from "@/hooks/agenticAI/useAgenticAI";
import { useAgentWorkspaceAdapter } from "@/hooks/agenticAI/useAgentWorkspaceAdapter";
import { useChatHistory } from "@/hooks/agenticAI/useChatHistory";
import { useLoadMoreOnVisible } from "@/hooks/useLoadMoreOnVisible";

import type {
  AnalysisReportArtifact,
  AnalysisResultRef
} from "@lokasi-ai-web-sdk/agent-ui";
import {
  activeAnalysisResult,
  LokasiAgentProvider,
  useLokasiAgent
} from "@lokasi-ai-web-sdk/agent-ui";
import { twMerge } from "tailwind-merge";

import AgenticAIChatArea from "./AgenticAIChatArea";
import AgenticAIMap from "./AgenticAIMap";
import AgenticAISidebar from "./AgenticAISidebar";
import {
  AGENTIC_AI_AGENT_TOPIC,
  AGENTIC_AI_HEADER_PADDING_Y,
  AGENTIC_AI_UNTITLED_CHAT,
  type ChatListLoadMore
} from "./constants";
import SidebarToggle from "./SidebarToggle";

import AgenticAISearchPage from "./search/AgenticAISearchPage";

/**
 * Everything below `LokasiAgentProvider`, so the live thread title from the SDK
 * session is read once and shared by the two places that show it — the sidebar
 * header and, when the sidebar is collapsed, the chat column header.
 */
const AgenticAIBody: FC<
  ReturnType<typeof useAgenticAI> & {
    reportStore: ReportSurfaceStore;
    analysisResultRef: MutableRefObject<AnalysisResultRef | null>;
  }
> = ({
  activeChatId,
  agentThreadId,
  isAgentChatOpen,
  isMapFullScreen,
  isSearchOpen,
  isSidebarOpen,
  handleNewChat,
  handleOpenAgentChat,
  handleSelectChat,
  handleToggleMapFullScreen,
  handleToggleSearch,
  handleToggleSidebar,
  reportStore,
  analysisResultRef
}) => {
  const { threadTitle, state, isRunning } = useLokasiAgent();
  // Read by the adapter's sync, which gets only the configuration.
  analysisResultRef.current = activeAnalysisResult(state);
  const { exportPdf, printCopyPortal } = useReportPdfExport();

  const reportState = useSyncExternalStore(
    reportStore.subscribe,
    reportStore.getSnapshot
  );
  const {
    chats,
    isLoading: isChatHistoryLoading,
    isInitialError: isChatHistoryError,
    isLoadMoreError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch: refetchChatHistory
  } = useChatHistory();
  const loadMore: ChatListLoadMore = {
    ref: useLoadMoreOnVisible({
      hasNextPage,
      isFetchingNextPage,
      isPaused: isLoadMoreError,
      fetchNextPage
    }),
    isFetching: isFetchingNextPage,
    isError: isLoadMoreError,
    // Not passed straight through: a click would hand its event to TanStack.
    onRetry: () => void fetchNextPage()
  };

  // A run can create a chat or change its title, so the list is stale after it.
  const queryClient = useQueryClient();
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) {
      void queryClient.invalidateQueries({
        queryKey: [CONVERSATIONS_QUERY_KEY]
      });
    }
    wasRunningRef.current = isRunning;
  }, [isRunning, queryClient]);

  const [isReportPanelExpanded, setIsReportPanelExpanded] = useState(false);
  const [closedSurfaceId, setClosedSurfaceId] = useState<string | null>(null);
  const [seenThreadId, setSeenThreadId] = useState(agentThreadId);

  const activeSurfaceId = reportState.activeSurfaceId;
  const isReportPanelOpen =
    activeSurfaceId !== null && activeSurfaceId !== closedSurfaceId;
  const isReportHidden = activeSurfaceId !== null && !isReportPanelOpen;

  if (seenThreadId !== agentThreadId) {
    setSeenThreadId(agentThreadId);
    setClosedSurfaceId(null);
    setIsReportPanelExpanded(false);
  }

  const handleCloseReportPanel = () => {
    setClosedSurfaceId(activeSurfaceId);
    setIsReportPanelExpanded(false);
  };

  const handleReopenReportPanel = () => setClosedSurfaceId(null);

  // The chat layout (narrow column beside the map) is for an open chat only.
  const isChatLayout = isAgentChatOpen && !isSearchOpen;

  const handleOpenReportSurface = useCallback(
    (surfaceId: string, toolCallId?: string) => {
      reportStore.showVersion(surfaceId, toolCallId);
      setClosedSurfaceId(null);
    },
    [reportStore]
  );

  const handleExportReportPdf = useCallback(
    (artifact: AnalysisReportArtifact) => {
      handleOpenReportSurface(artifact.artifactId, artifact.toolCallId);
      const surface = reportStore.getSurface(artifact.artifactId);
      if (surface) exportPdf(surface);
    },
    [exportPdf, handleOpenReportSurface, reportStore]
  );

  const renderAnalysisReportArtifact = useCallback(
    (artifact: AnalysisReportArtifact) => {
      const hasSurface = Boolean(reportStore.getSurface(artifact.artifactId));

      return (
        <ReportArtifactCard
          artifact={artifact}
          onOpen={
            hasSurface
              ? () =>
                  handleOpenReportSurface(
                    artifact.artifactId,
                    artifact.toolCallId
                  )
              : undefined
          }
          onExport={
            hasSurface
              ? (format) => {
                  if (format === "pdf") handleExportReportPdf(artifact);
                }
              : undefined
          }
        />
      );
    },
    [handleExportReportPdf, handleOpenReportSurface, reportStore]
  );

  return (
    <>
      {printCopyPortal}

      {isSidebarOpen && !isMapFullScreen && (
        <AgenticAISidebar
          chats={chats}
          activeChatId={activeChatId}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
          onCollapse={handleToggleSidebar}
          isLoading={isChatHistoryLoading}
          isError={isChatHistoryError}
          onRetry={() => void refetchChatHistory()}
          loadMore={loadMore}
          isSearchOpen={isSearchOpen}
          onToggleSearch={handleToggleSearch}
        />
      )}

      {!isMapFullScreen && (
        <div
          className={twMerge(
            "flex min-w-0 flex-1 flex-col bg-white",
            isChatLayout && "bg-white lg:flex-[0_1_520px]",
            isMapFullScreen && ""
          )}
        >
          {!isSidebarOpen && (
            <div
              className={twMerge(
                "flex shrink-0 items-center gap-2",
                AGENTIC_AI_HEADER_PADDING_Y,
                isChatLayout
                  ? "border-b border-gainsboro ml-4 pr-4"
                  : "bg-white-smoke px-6"
              )}
            >
              <SidebarToggle
                isSidebarOpen={isSidebarOpen}
                onToggle={handleToggleSidebar}
              />

              {isChatLayout && (
                <h2 className="truncate text-sm font-bold text-[#878787]">
                  {threadTitle ?? AGENTIC_AI_UNTITLED_CHAT}
                </h2>
              )}
            </div>
          )}

          {/* Stays mounted while search is open, so a draft and a running chat survive. */}
          <div
            data-testid="agentic-ai-chat-area"
            className={isSearchOpen ? "hidden" : "contents"}
          >
            <AgenticAIChatArea
              isAgentChatOpen={isAgentChatOpen}
              isHidden={isSearchOpen}
              onOpenAgentChat={handleOpenAgentChat}
              renderAnalysisReportArtifact={renderAnalysisReportArtifact}
            />
          </div>

          {isSearchOpen && (
            <AgenticAISearchPage
              isSidebarOpen={isSidebarOpen}
              onSelectChat={handleSelectChat}
            />
          )}
        </div>
      )}

      {isAgentChatOpen && (
        <div
          data-testid="agentic-ai-map-column"
          className={twMerge(
            "relative hidden min-w-0 flex-1 m-5 ml-4",
            !isSearchOpen && "lg:block"
          )}
        >
          <AgenticAIMap
            isFullScreen={isMapFullScreen}
            onToggleFullScreen={handleToggleMapFullScreen}
            isReportShortcutVisible={isReportHidden}
          />

          {isReportPanelOpen && (
            <button
              type="button"
              onClick={handleToggleMapFullScreen}
              aria-label={
                isMapFullScreen
                  ? "Show conversation panel"
                  : "Hide conversation panel"
              }
              className="absolute left-4 top-4 z-10 rounded-md border border-gainsboro bg-white p-1.5 shadow-sm hover:bg-white-smoke [&>svg]:h-6 [&>svg]:w-6
                 [&>svg]:fill-dim-gray"
            >
              {isMapFullScreen ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
            </button>
          )}

          {isReportPanelOpen && (
            <div
              className={twMerge(
                "absolute inset-y-0 right-0 z-10 m-3 transition-all duration-300",
                // Runs once, when the panel mounts, so the panel arrives
                // instead of appearing.
                "animate-in fade-in slide-in-from-right-6 motion-reduce:animate-none",
                isReportPanelExpanded ? "left-0 w-auto" : "w-[514px]"
              )}
            >
              <AnalysisReportPanel
                store={reportStore}
                onClose={handleCloseReportPanel}
                isExpanded={isReportPanelExpanded}
                onToggleMaximize={() =>
                  setIsReportPanelExpanded((prev) => !prev)
                }
              />
            </div>
          )}

          {isReportHidden && (
            <button
              type="button"
              onClick={handleReopenReportPanel}
              data-testid="report-restore-button"
              className="absolute right-4 top-4 z-10 flex items-center gap-2 rounded-xl border border-gainsboro bg-white px-4 py-3 shadow-sm hover:bg-teal-50 [&>svg]:h-6 [&>svg]:w-6 [&>svg]:fill-teal-500"
            >
              <BarChartIcon />
              <span className="text-base font-semibold text-teal-500">
                Analysis Summary
              </span>
            </button>
          )}
        </div>
      )}
    </>
  );
};

const AgenticAI = () => {
  const { adapter, resetWorkspace, analysisResultRef } =
    useAgentWorkspaceAdapter();
  const agenticAI = useAgenticAI(resetWorkspace);

  // One store per thread: the old processor is disposed, so no surface leaks
  // into the next conversation.
  const reportStoreRef = useRef<{
    threadId: string;
    store: ReportSurfaceStore;
  } | null>(null);
  if (reportStoreRef.current?.threadId !== agenticAI.agentThreadId) {
    reportStoreRef.current = {
      threadId: agenticAI.agentThreadId,
      store: createReportSurfaceStore()
    };
  }
  const reportStore = reportStoreRef.current.store;
  const reportAdapter = useAnalysisReportAdapter(reportStore);

  useEffect(() => () => reportStore.reset(), [reportStore]);

  // Declared on every run, not once per session: the backend reads it per
  // request. The inline catalog is a development affordance — the server
  // rejects one wherever inline catalogs are switched off — so outside dev
  // only the registered id goes.
  const a2ui = {
    protocolVersion: "v0.9.1",
    catalogId: REPORT_CATALOG_ID
  };

  return (
    <div className="flex h-full bg-white">
      <LokasiAgentProvider
        connection={{
          // A prefix in front of the backend v2 conversation routes; they are
          // mounted at the site root.
          endpoint: "",
          httpClient: axiosInstance,
          threadId: agenticAI.agentThreadId
        }}
        context={{ topics: AGENTIC_AI_AGENT_TOPIC, a2ui }}
        workspaceAdapter={adapter}
        analysisReportAdapter={reportAdapter}
      >
        <AgenticAIBody
          {...agenticAI}
          reportStore={reportStore}
          analysisResultRef={analysisResultRef}
        />
      </LokasiAgentProvider>
    </div>
  );
};

export default AgenticAI;
